import { useEffect, useRef, useState } from 'react'
import Icon from '../Icon'
import PhotoPicker from './PhotoPicker'
import AddChooser, { type AddRoute } from './AddChooser'
import { priceToInput } from '../../../shared/money'
import {
  addPhotos, createPlant, createSpecies, getPlant, identify, listSpecies,
  updatePlant, type Photo, type Plant, type Species, type Suggestion,
} from '../../lib/ppc'

/**
 * Adding and editing one plant.
 *
 * ── The asking price field ────────────────────────────────────────────────────
 *
 * `price` is a STRING in component state and stays one until save. It is not
 * seeded with '0', it is never run through Number() on change, and an empty box
 * is sent as an empty string, which the server stores as NULL — not priced.
 *
 * That is the entire fix. The old field held a number, so clearing it produced
 * NaN-or-zero and the zero reappeared under the cursor; and an untouched field
 * meant a plant was saved priced at nothing. Both are impossible here by type.
 */
export default function PlantEditor({
  plantId, onDone, onCancel,
}: {
  plantId: string | null
  onDone: (plant: Plant) => void
  onCancel: () => void
}) {
  // Handed to PhotoPicker so the chooser can open the camera or the library
  // directly, without the picker having to know why.
  const openCamera = useRef<(() => void) | null>(null)
  const openLibrary = useRef<(() => void) | null>(null)

  const [species, setSpecies] = useState<Species[]>([])
  const [photos, setPhotos] = useState<Photo[]>([])
  const [pending, setPending] = useState<string[]>([])
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [suggestion, setSuggestion] = useState<Suggestion | null>(null)
  const [identifying, setIdentifying] = useState(false)
  /*
   * Which way in. Null means the chooser is still showing, and only ever for a
   * NEW plant — editing an existing one goes straight to the form, because the
   * question "how do you want to add this" has already been answered.
   */
  const [route, setRoute] = useState<AddRoute | null>(plantId ? 'manual' : null)
  const [identifyReady, setIdentifyReady] = useState(true)

  const [code, setCode] = useState('')
  const [nickname, setNickname] = useState('')
  const [speciesId, setSpeciesId] = useState('')
  const [price, setPrice] = useState('')      // string, deliberately
  const [cost, setCost] = useState('')        // string, deliberately
  const [height, setHeight] = useState('')
  const [potSize, setPotSize] = useState('')
  const [condition, setCondition] = useState('')
  const [location, setLocation] = useState('')
  const [source, setSource] = useState('')
  const [notes, setNotes] = useState('')
  const [availability, setAvailability] = useState('available')
  const [isBatch, setIsBatch] = useState(false)
  const [quantity, setQuantity] = useState('1')

  useEffect(() => {
    void listSpecies().then(setSpecies).catch(() => {})
  }, [])

  /*
   * Ask ONCE whether identification is configured, by sending a request with no
   * photos: the handler answers 503 `identify_unconfigured` before it looks at
   * the body, and 400 `no_photos` when it is switched on. Cheaper and more
   * honest than a separate status endpoint, and it means the chooser never
   * offers a route that cannot work.
   */
  useEffect(() => {
    void identify([]).catch((problem: unknown) => {
      const code = (problem as { code?: string })?.code
      if (code === 'identify_unconfigured') setIdentifyReady(false)
    })
  }, [])

  useEffect(() => {
    if (!plantId) return
    void getPlant(plantId).then(({ plant, photos: shots }) => {
      setCode(plant.code)
      setNickname(plant.nickname ?? '')
      setSpeciesId(plant.species_id ?? '')
      setPrice(priceToInput(plant.asking_price_cents))
      setCost(priceToInput(plant.cost_cents))
      setHeight(plant.height_cm === null ? '' : String(plant.height_cm))
      setPotSize(plant.pot_size ?? '')
      setCondition(plant.condition ?? '')
      setLocation(plant.location ?? '')
      setSource(plant.source ?? '')
      setNotes(plant.notes ?? '')
      setAvailability(plant.availability)
      setIsBatch(!!plant.is_batch)
      setQuantity(String(plant.quantity))
      setPhotos(shots)
    }).catch(() => setError('That plant could not be loaded.'))
  }, [plantId])

  /** Ask the model what this is, then PREFILL. Nothing is saved by doing this. */
  async function runIdentify() {
    const ids = plantId ? photos.map((p) => p.upload_id) : pending
    if (ids.length === 0) {
      setError('Take a photo first.')
      return
    }
    setIdentifying(true)
    setError(null)
    try {
      const result = await identify(ids)
      setSuggestion(result)
      // Only fill what is still EMPTY. A suggestion must never overwrite
      // something Dillon typed himself.
      if (!nickname && result.commonName) setNickname(result.commonName)
    } catch (problem) {
      setError(problem instanceof Error ? problem.message : 'Identification did not work.')
    } finally {
      setIdentifying(false)
    }
  }

  /**
   * Turn the suggestion into a species profile — at 'ai_suggested', never
   * confirmed.
   *
   * If the model recognised something already in the catalogue, LINK to it
   * rather than creating a near-duplicate. Two profiles for one plant is how a
   * nursery ends up with "Foxtail palm" and "Foxtail Palm" holding half the
   * stock each, and nothing in the app would ever tell you.
   */
  async function acceptSuggestion() {
    if (!suggestion) return
    if (suggestion.matchedSpeciesId) {
      setSpeciesId(suggestion.matchedSpeciesId)
      setSuggestion(null)
      return
    }
    const created = await createSpecies({
      commonName: suggestion.commonName ?? nickname ?? 'Unidentified plant',
      scientificName: suggestion.scientificName ?? undefined,
      cultivar: suggestion.cultivar ?? undefined,
      description: suggestion.characteristics ?? undefined,
      care: suggestion.care ?? undefined,
      matureSize: suggestion.matureSize ?? undefined,
      idStatus: 'ai_suggested',
    })
    setSpecies([created, ...species])
    setSpeciesId(created.id)
    setSuggestion(null)
  }

  async function save() {
    setSaving(true)
    setError(null)
    try {
      const draft = {
        nickname: nickname || null,
        speciesId: speciesId || null,
        // Sent exactly as typed. Empty string means "not priced".
        askingPrice: price,
        cost,
        heightCm: height.trim() === '' ? null : Number(height),
        potSize: potSize || null,
        condition: condition || null,
        location: location || null,
        source: source || null,
        notes: notes || null,
        availability,
        isBatch,
        quantity: isBatch ? Math.max(0, Number(quantity) || 0) : 1,
      }

      if (plantId) {
        const { plant } = await updatePlant(plantId, draft)
        onDone(plant)
      } else {
        const plant = await createPlant(draft)
        // Photos taken before the plant existed are attached now.
        if (pending.length) await addPhotos(plant.id, pending)
        onDone(plant)
      }
    } catch (problem) {
      setError(problem instanceof Error ? problem.message : 'That could not be saved.')
    } finally {
      setSaving(false)
    }
  }

  if (route === null) {
    return (
      <AddChooser
        identifyReady={identifyReady}
        onCancel={onCancel}
        onChoose={(chosen) => {
          setRoute(chosen)
          // The camera and library routes open the picker straight away: the
          // point of choosing them was not to look at a form first.
          if (chosen === 'camera') setTimeout(() => openCamera.current?.(), 0)
          if (chosen === 'upload') setTimeout(() => openLibrary.current?.(), 0)
        }}
      />
    )
  }

  return (
    <section className="card">
      <h2 style={{ fontSize: 26, marginTop: 0 }}>{plantId ? 'Edit plant' : 'Add a plant'}</h2>
      <p className="muted small" style={{ marginTop: 0 }}>One record per physical pot. Keep its tag attached.</p>

      <PhotoPicker
        plantId={plantId} photos={photos} onChange={setPhotos}
        pending={pending} onPending={setPending}
        openCamera={openCamera} openLibrary={openLibrary}
      />

      <div className="row" style={{ marginTop: 12 }}>
        <button type="button" onClick={() => void runIdentify()} disabled={identifying}>
          <Icon name="sparkle" size={15} /> {identifying ? 'Looking…' : 'Identify from photos'}
        </button>
      </div>

      {suggestion && (
        <div className="note-panel" style={{ marginTop: 12 }}>
          <h3 style={{ marginTop: 0 }}><Icon name="sparkle" size={17} /> AI suggestion — review it</h3>
          <p className="muted small">
            A suggestion, not a confirmation. Check it before it goes on a tag.
            Confidence: <b>{suggestion.confidence}</b>
          </p>
          {/*
            * Say so when this is something already in the catalogue. Accepting
            * then links to that profile rather than making a second one, and
            * Dillon needs to know which of the two is about to happen.
            */}
          {suggestion.matchedSpeciesName && (
            <p className="small">
              <Icon name="check" size={14} /> Already in your species list as{' '}
              <b>{suggestion.matchedSpeciesName}</b>.
            </p>
          )}
          {/*
            * Where the figures came from. A measurement out of a published
            * checklist and a model's recollection of one are not the same
            * claim, and this is the line that says which you are looking at.
            */}
          {suggestion.reference && (
            <p className="small">
              <Icon name="book" size={14} /> Size and characteristics from{' '}
              <b>{suggestion.reference.source}</b>
              {suggestion.reference.corrected && ' — spelling corrected against it'}.
            </p>
          )}
          <dl className="pairs">
            {suggestion.commonName && <><dt>Common name</dt><dd>{suggestion.commonName}</dd></>}
            {suggestion.scientificName && <><dt>Scientific name</dt><dd><i>{suggestion.scientificName}</i></dd></>}
            {suggestion.cultivar && <><dt>Cultivar</dt><dd>{suggestion.cultivar}</dd></>}
            {suggestion.matureSize && <><dt>Mature size</dt><dd>{suggestion.matureSize}</dd></>}
            {suggestion.care && <><dt>Care</dt><dd>{suggestion.care}</dd></>}
          </dl>
          <div className="row">
            <button type="button" className="primary" onClick={() => void acceptSuggestion()}>
              {suggestion.matchedSpeciesId ? 'Link to that species' : 'Use it as the species'}
            </button>
            <button type="button" onClick={() => setSuggestion(null)}>Discard</button>
          </div>
        </div>
      )}

      <div className="two-up">
        <label>
          Plant tag
          <input value={code || 'Assigned when you save'} readOnly aria-readonly />
          <span className="hint">
            {code ? 'Permanent. It goes on the label and never changes.' : 'The next tag in the sequence, assigned on save.'}
          </span>
        </label>
        <label>
          Common name / working name
          <input value={nickname} onChange={(e) => setNickname(e.target.value)} placeholder="Ruby ficus by the gate" />
        </label>
      </div>

      <label>
        Species profile
        <select value={speciesId} onChange={(e) => setSpeciesId(e.target.value)}>
          <option value="">— none yet —</option>
          {species.map((s) => (
            <option key={s.id} value={s.id}>
              {s.common_name}{s.id_status !== 'confirmed' ? ' (unconfirmed)' : ''}
            </option>
          ))}
        </select>
      </label>

      <div className="two-up">
        <label>
          Height above soil (cm)
          <input type="text" inputMode="decimal" value={height} onChange={(e) => setHeight(e.target.value)} placeholder="e.g. 90" />
        </label>
        <label>
          Pot size
          <input value={potSize} onChange={(e) => setPotSize(e.target.value)} placeholder="e.g. 3 gallon" />
        </label>
      </div>

      <div className="two-up">
        <label>
          Asking price ($)
          <input
            /*
             * TEXT, not number, and never coerced on change.
             *
             * A number input fights the cursor on the way to a decimal and
             * cannot hold the empty string the way this field must. The value
             * is parsed exactly once, on the server, at save.
             */
            type="text" inputMode="decimal" value={price} placeholder="Leave blank if not priced yet"
            onChange={(e) => setPrice(e.target.value)}
          />
          <span className="hint">Blank means not priced. It shows as a dash, never $0.</span>
        </label>
        <label>
          Status
          <select value={availability} onChange={(e) => setAvailability(e.target.value)}>
            <option value="available">Available</option>
            <option value="reserved">Reserved</option>
            <option value="sold">Sold</option>
            <option value="not_for_sale">Not for sale</option>
          </select>
        </label>
      </div>

      <div className="two-up">
        <label>
          Cost
          <input type="text" inputMode="decimal" value={cost} onChange={(e) => setCost(e.target.value)} placeholder="optional" />
        </label>
        <label>
          Condition
          <input value={condition} onChange={(e) => setCondition(e.target.value)} placeholder="Healthy" />
        </label>
      </div>

      <div className="two-up">
        <label>
          Location
          <input value={location} onChange={(e) => setLocation(e.target.value)} placeholder="Back bench" />
        </label>
        <label>
          Source
          <input value={source} onChange={(e) => setSource(e.target.value)} placeholder="Where it came from" />
        </label>
      </div>

      <label className="checkbox">
        <input type="checkbox" checked={isBatch} onChange={(e) => setIsBatch(e.target.checked)} />
        This is a batch of small interchangeable plants
      </label>

      {isBatch && (
        <label>
          How many
          <input type="text" inputMode="numeric" value={quantity} onChange={(e) => setQuantity(e.target.value)} />
        </label>
      )}

      <label>
        Notes / condition / pot included
        <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={3} />
      </label>

      {error && <p className="error">{error}</p>}

      <div className="row">
        <button type="button" className="primary" onClick={() => void save()} disabled={saving}>
          {saving ? 'Saving…' : plantId ? 'Save changes' : 'Add this plant'}
        </button>
        <button type="button" onClick={onCancel}>Cancel</button>
      </div>
    </section>
  )
}
